import React, { useState, useRef } from "react";
import {
  Facebook,
  Instagram,
  Linkedin,
  Phone,
  ChevronDown,
  Menu,
  X,
} from "lucide-react";

// Hover-triggered dropdown
const HoverDropdown = ({ label, items, blue }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        className={`flex items-center gap-1 text-[13.5px] font-medium transition-colors duration-200 outline-none ${
          open ? "text-[#0E94D6]" : "text-[#1f2937] hover:text-[#0E94D6]"
        }`}
      >
        {label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* invisible bridge so cursor can travel from trigger to menu */}
      <div
        className={`absolute left-1/2 -translate-x-1/2 top-full h-3 w-full ${
          open ? "" : "pointer-events-none"
        }`}
      />

      <div
        className={`absolute left-1/2 -translate-x-1/2 top-[calc(100%+10px)] min-w-[220px] rounded-xl bg-white border border-gray-100 shadow-[0_10px_30px_rgba(0,0,0,0.08)] py-2 origin-top transition-all duration-200 ${
          open
            ? "opacity-100 scale-100 visible"
            : "opacity-0 scale-95 invisible"
        }`}
      >
        {/* small caret */}
        <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-100 rotate-45" />
        {items.map((item) => (
          <a
            key={item.label}
            href={item.href}
            className="block px-4 py-2.5 text-[13.5px] text-[#1f2937] hover:bg-[#F3F8FB] hover:text-[#0E94D6] transition-colors"
          >
            {item.label}
          </a>
        ))}
      </div>
    </div>
  );
};

// X (Twitter) icon — lucide doesn't ship a "X" brand icon, so use inline svg
const XIcon = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
  </svg>
);

const NAV_LINKS = [
  { label: "About Us", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Gallery", href: "#gallery" },
];

const HOMEOWNERS_ITEMS = [
  { label: "Get a Quote", href: "#quote" },
  { label: "Book a Service", href: "#book" },
  { label: "Track Project", href: "#track" },
  { label: "FAQ", href: "#faq" },
];

const CONTRACTORS_ITEMS = [
  { label: "Pro Registration", href: "#register" },
  { label: "Pro Login", href: "#pro-login" },
  { label: "Resources", href: "#resources" },
  { label: "Become a Partner", href: "#partner" },
];

const SOCIALS = [
  {
    label: "Facebook",
    href: "https://facebook.com/meinhaus",
    Icon: Facebook,
  },
  {
    label: "Instagram",
    href: "https://instagram.com/meinhaus",
    Icon: Instagram,
  },
  { label: "X", href: "https://x.com/meinhaus", Icon: XIcon },
  {
    label: "LinkedIn",
    href: "https://linkedin.com/company/meinhaus",
    Icon: Linkedin,
  },
];

const BLUE = "#0E94D6"; // brand primary blue (matches logo)
const ORANGE = "#F26B1F"; // brand orange (matches logo)

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileHomeownersOpen, setMobileHomeownersOpen] = useState(false);
  const [mobileContractorsOpen, setMobileContractorsOpen] = useState(false);

  return (
    <header className="w-full sticky top-0 z-50">
      {/* top blue accent line */}
      <div className="h-[3px] w-full" style={{ backgroundColor: BLUE }} />

      <nav className="bg-white shadow-[0_2px_10px_rgba(0,0,0,0.06)]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-3">
          <div className="flex items-center justify-between gap-10">
            {/* Logo */}
            <a href="/" className="flex items-center shrink-0" aria-label="MeinHaus home">
              <img
                src="/logo.svg"
                alt="MeinHaus — Online General Contractor"
                className="h-[38px] w-auto select-none"
                draggable={false}
              />
            </a>

            {/* Pill container wrapping nav + actions */}
            <div
              className="hidden lg:flex flex-1 items-center rounded-full border-2 pl-6 pr-1 py-1 ml-6 whitespace-nowrap"
              style={{ borderColor: BLUE }}
            >
              {/* Nav links — centered */}
              <div className="flex flex-1 items-center justify-center gap-7">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  className="text-[13.5px] font-medium text-[#1f2937] hover:text-[#0E94D6] transition-colors duration-200 relative group"
                >
                  {l.label}
                  <span
                    className="absolute left-0 -bottom-1 h-[2px] w-0 group-hover:w-full transition-all duration-300"
                    style={{ backgroundColor: BLUE }}
                  />
                </a>
              ))}

              {/* For Homeowners dropdown */}
              <HoverDropdown
                label="For Homeowners"
                items={HOMEOWNERS_ITEMS}
                blue={BLUE}
              />

              {/* For Contractors dropdown */}
              <HoverDropdown
                label="For Contractors"
                items={CONTRACTORS_ITEMS}
                blue={BLUE}
              />
              </div>

              {/* Right side: socials + phone + sign in */}
              <div className="flex items-center gap-2.5">
              <div className="flex items-center gap-1.5">
                {SOCIALS.map(({ label, href, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="h-7 w-7 rounded-full border-[1.5px] flex items-center justify-center transition-all duration-200 hover:scale-110 hover:bg-[#0E94D6] hover:text-white"
                    style={{ borderColor: BLUE, color: BLUE }}
                  >
                    <Icon className="h-3 w-3" />
                  </a>
                ))}
              </div>

              {/* divider */}
              <span className="h-6 w-px bg-gray-200 mx-1" />

              <a
                href="tel:+18006256268"
                className="flex items-center gap-1.5 font-semibold text-[13px] hover:opacity-80 transition-opacity px-1"
                style={{ color: BLUE }}
              >
                <Phone className="h-3.5 w-3.5" fill={BLUE} strokeWidth={0} />
                (800) 625-6268
              </a>

              <a
                href="#signin"
                className="px-6 py-2 rounded-full text-white font-semibold text-[13px] tracking-wide shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap"
                style={{ backgroundColor: ORANGE }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#D85B14")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = ORANGE)
                }
              >
                Sign In
              </a>
              </div>
            </div>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-gray-100 bg-white">
            <div className="px-5 py-4 space-y-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  className="block px-3 py-2.5 rounded-md text-[15px] font-medium text-[#1f2937] hover:bg-[#F3F8FB] hover:text-[#0E94D6]"
                >
                  {l.label}
                </a>
              ))}

              <button
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-[15px] font-medium text-[#1f2937] hover:bg-[#F3F8FB] hover:text-[#0E94D6]"
                onClick={() => setMobileHomeownersOpen((v) => !v)}
              >
                For Homeowners
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    mobileHomeownersOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {mobileHomeownersOpen && (
                <div className="pl-4 space-y-1">
                  {HOMEOWNERS_ITEMS.map((i) => (
                    <a
                      key={i.label}
                      href={i.href}
                      className="block px-3 py-2 rounded-md text-[14px] text-gray-600 hover:bg-[#F3F8FB] hover:text-[#0E94D6]"
                    >
                      {i.label}
                    </a>
                  ))}
                </div>
              )}

              <button
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-[15px] font-medium text-[#1f2937] hover:bg-[#F3F8FB] hover:text-[#0E94D6]"
                onClick={() => setMobileContractorsOpen((v) => !v)}
              >
                For Contractors
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    mobileContractorsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {mobileContractorsOpen && (
                <div className="pl-4 space-y-1">
                  {CONTRACTORS_ITEMS.map((i) => (
                    <a
                      key={i.label}
                      href={i.href}
                      className="block px-3 py-2 rounded-md text-[14px] text-gray-600 hover:bg-[#F3F8FB] hover:text-[#0E94D6]"
                    >
                      {i.label}
                    </a>
                  ))}
                </div>
              )}

              <div className="pt-3 border-t border-gray-100 mt-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {SOCIALS.map(({ label, href, Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="h-9 w-9 rounded-full border-2 flex items-center justify-center transition-colors duration-200 hover:bg-[#0E94D6] hover:text-white"
                      style={{ borderColor: BLUE, color: BLUE }}
                    >
                      <Icon className="h-[15px] w-[15px]" />
                    </a>
                  ))}
                </div>
                <a
                  href="tel:+18006256268"
                  className="flex items-center gap-2 font-semibold text-[14px]"
                  style={{ color: BLUE }}
                >
                  <Phone className="h-4 w-4" fill={BLUE} strokeWidth={0} />
                  (800) 625-6268
                </a>
              </div>

              <a
                href="#signin"
                className="block w-full text-center mt-3 px-6 py-3 rounded-md text-white font-semibold text-[14px] shadow-sm"
                style={{ backgroundColor: ORANGE }}
              >
                Sign In
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
